import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { showError, showSuccess } from "toast/toast";
import SignageBrandCategoryForm from "components/AddEditBrandCategory";
import { uploadCloudFile, getCloudFileById, resolveImageId, hardDeleteCloudFile } from "components/Imageshelper";
type SelectOption = { value: string; label: string };
import { getAuthCookie } from "../../utils/authCookies";
const STATUS_OPTIONS: SelectOption[] = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

// async function uploadBrandImage(file: File, token: string) {
//   const formData = new FormData();
//   formData.append("file", file);
//   formData.append("uploadDir", "brands");
//   return new Promise<string>((resolve, reject) => {
//     const xhr = new XMLHttpRequest();
//     xhr.open("POST", `${process.env.REACT_APP_API_URL}/images/upload`, true);
//     xhr.setRequestHeader("Authorization", `Bearer ${token}`);
//     xhr.onload = () => {
//       try {
//         if (xhr.status >= 200 && xhr.status < 300) {
//           const resp = JSON.parse(xhr.responseText || "{}");
//           resolve(`${resp.path}`);
//         } else reject(new Error(`Upload failed (${xhr.status})`));
//         }catch (e) { reject(e); }
//     };
//     xhr.onerror = () => reject(new Error("Upload network error"));
//     xhr.send(formData);
//   });
// }

const RealtorAddEditBrand: React.FC = () => {
  const { brandId } = useParams<{ brandId: string }>();
  const isEdit = !!brandId;
  const navigate = useNavigate();
  const location = useLocation();
  const isTeammember = location.pathname.includes('/teammember');
  const baseRoute = isTeammember ? '/teammember' : '/realtor';
  const token = getAuthCookie("sessionToken") || "";

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  // ADD next to your other state:
  const [cloudFileId, setCloudFileId] = useState<string | null>(null);
  
  // Categories state
  const [categoryOptions, setCategoryOptions] = useState<SelectOption[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<SelectOption[]>([]);

  const formik = useFormik<{ name: string; statuss: string; image: File | null }>({
    enableReinitialize: true,
    initialValues: { name: "", statuss: "Active", image: null },
    validationSchema: Yup.object({
      name: Yup.string().min(2).max(50).required("Name is required"),
      // statuss: Yup.string().required("Status is required"),
    }),
    onSubmit: async (values) => {
      setSubmitting(true);
      try {
        // let imagePath =
        //   previewUrl?.replace(/^https?:\/\/[^/]+\/(uploads\/.*)/, "$1") || "";

        // if (values.image) {
        //   imagePath = await uploadBrandImage(values.image, token);
        // }

        // 1) Decide the file id (upload if user picked a new file)
        let imageFileId = cloudFileId || ""; // keep existing if present
        
        // ✅ Delete old image if a new one is being uploaded
        if (values.image instanceof File && cloudFileId && /^[0-9a-f]{24}$/i.test(cloudFileId)) {
          try {
            await hardDeleteCloudFile(cloudFileId, token);
            console.log(`Old brand image ${cloudFileId} deleted successfully`);
          } catch (deleteError) {
            console.warn(`Failed to delete old brand image ${cloudFileId}:`, deleteError);
            // Continue even if delete fails - don't block the update
          }
        }
        
        // Upload new image
        if (values.image instanceof File) {
          const uploaded = await uploadCloudFile({ file: values.image, token });
          imageFileId = uploaded.id;
        }

        const generated_id = await resolveImageId(values.image, token, imageFileId);

        const payload = {
          name: values.name,
          image: generated_id,
          status: values.statuss === "Active",
          categories: selectedCategories.map(c => c.value), // ✅ Pass selected category IDs
        };

        const url = isEdit
          ? `${process.env.REACT_APP_API_URL}/brands/${brandId}`
          : `${process.env.REACT_APP_API_URL}/brands`;
        const method = isEdit ? "PUT" : "POST";

        const res = await fetch(url, {
          method,
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok || data?.error) throw new Error(data?.error || data?.message || "Save failed");

        showSuccess(isEdit ? "Brand updated successfully!" : "Brand added successfully!");
        navigate(`${baseRoute}/brands`);
      } catch (e: any) {
        showError(e?.message || (isEdit ? "Failed to update brand." : "Failed to add brand."));
      } finally {
        setSubmitting(false);
      }
    },
  });

  // ✅ Fetch categories for all brands
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/categories`, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json?.error || "Failed to fetch categories");
        
        if (!abort) {
          // Handle different API response formats
          const list: any[] = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];
          
          // Map to SelectOption format
          const normalized = list
            .map((it: any) => ({
              value: String(it?._id ?? it?.id ?? ""),
              label: String(it?.name ?? it?.label ?? "Unnamed Category"),
            }))
            .filter(opt => opt.value); // Remove empty values
          
          setCategoryOptions(normalized);
        }
      } catch (err: any) {
        console.error("Failed to fetch categories:", err);
        if (!abort) showError(err?.message || "Failed to fetch categories");
      }
    })();

    return () => { abort = true; };
  }, [token]);

  useEffect(() => {
    if (!isEdit) return;
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${process.env.REACT_APP_API_URL}/brands/${brandId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok || data?.error) throw new Error(data?.error || data?.message || "Fetch failed");
        if (!cancel) {
          formik.setValues({
            name: data?.name || "",
            statuss: data?.status ? "Active" : "Inactive",
            image: null,
          });
          const img = data?.image || data?.image_file_id || data?.cloud_file_id || "";
          if (img) {
            // Legacy: stored a static /uploads path
            if (typeof img === "string" && (img.startsWith("uploads/") || img.includes("/uploads/"))) {
              setPreviewUrl(`${process.env.REACT_APP_BASE_URL}${img.replace(/^\//, "")}`);
              setCloudFileId(null);
            } else {
              // New: stored a cloud-drive file id
              try {
                const cf = await getCloudFileById(String(img), token);
                setPreviewUrl(cf.url || null);
                setCloudFileId(cf.id);
              } catch {
                // fallback: show nothing if file fetch fails
                setPreviewUrl(null);
                setCloudFileId(String(img));
              }
            }
          } else {
            setPreviewUrl(null);
            setCloudFileId(null);
          }
          
          // ✅ Prefill selected categories
          if (data?.categories && Array.isArray(data.categories) && categoryOptions.length > 0) {
            const categoryIds = data.categories.map((id: any) => String(id));
            const selected = categoryOptions.filter(opt => categoryIds.includes(opt.value));
            setSelectedCategories(selected);
          }
        }
      } catch (e: any) {
        showError(e?.message || "Error fetching brand");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [brandId, isEdit, token, categoryOptions]);

  if (loading) return <div className="content container">Loading…</div>;

  return (
    <SignageBrandCategoryForm
      title={isEdit ? "Edit Brand" : "Add Brand"}
      blockTitle="Brand Details"
      infoTitle="Brand Information"
      imageTitle="Brand Image"
      nameLabel="Name"
      formik={formik}
      statusOptions={STATUS_OPTIONS}
      submitting={submitting}
      onBack={() => navigate(-1)}
      onSubmit={() => formik.handleSubmit()}
      previewUrl={previewUrl}
      baseRoute={baseRoute}
      onDropFiles={(files: any) => {
        const f = files[0];
        if (f) { formik.setFieldValue("image", f); setPreviewUrl(URL.createObjectURL(f)); }
      }}
      onPickFile={(file: any) => {
        formik.setFieldValue("image", file);
        setPreviewUrl(file ? URL.createObjectURL(file) : null);
      }}
      
      // ===== Categories =====
      categoryOptions={categoryOptions}
      selectedCategories={selectedCategories}
      onChangeCategories={(vals) => setSelectedCategories(vals)}
      onClearAllCategories={() => setSelectedCategories([])}
    />
  );
};

export default RealtorAddEditBrand;
